/**
 * Pointer gestures of the curve editor (W2-04b curve-editor; DESIGN.md §4.7, §5.6.2, §8.2).
 *
 * Rules (DESIGN.md §4.7):
 *  - drag a key: vertical = time, snapped to the beat grid; horizontal = value. Ctrl snaps the value to round
 *    numbers (in the friendly unit), Alt turns snapping off (time on the 1/1920 grid, value unrounded), Shift locks
 *    the drag to the direction chosen by the first 4 px. Keys never pass each other: `Core.keys.moveKeys` stops one
 *    snap step before a neighbour. Every selected key moves in time together (also on other columns, EVT-19); the
 *    value changes on the selected keys of the dragged lane;
 *  - a jump key's half-diamonds edit the arrive (`vIn`) or leave (`vOut`) value; after a Stay segment the arrive
 *    value is the stayed value (the core keeps it), so that half only moves the key in time;
 *  - selection: a press selects (Shift adds, Ctrl adds an unselected item). Ctrl on an already selected item is
 *    decided on release: without movement it toggles the item off, with movement it drags the selection with
 *    round values (Ctrl is also the round-values modifier);
 *  - a segment drag moves the segment (and the other selected segments) in time;
 *  - Bezier handles: x (progress) clamped to [0, 1], y free; values rounded to 0.01 (Ctrl 0.05, Alt free);
 *  - slice handles pick [left, right] of the numbered curve; a degenerate slice (f(left) = f(right), which Phira
 *    cannot normalise) is refused: the last valid slice stays and a warning is shown.
 * Every drag is ONE live `Gesture` of the DocStore (preview, timeline and inspector update while dragging; one undo
 * step on release; Esc cancels). The transaction bodies are the shared core commands, so link groups, X/Y link
 * mirroring and model invariants are kept by the same code as everywhere else.
 */

import { Core } from '../../core/commands/index';
import { Ease } from '../../core/ease/index';
import type { BezierPoints, Ease as EaseValue } from '../../core/ease/types';
import type { KeyId } from '../../core/model/types';
import type { TxBuilder } from '../../core/ops/types';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { MessageRef } from '../../core/util/types';
import type { UiService } from '../../app/registry/types';
import type { DocStore, EditorStoreApi, Gesture, TxnMeta } from '../../state/types';
import type { TimeAxis, TimelinePointerEvent } from '../../render/types';
import { FINEST_DIV, snapBeat } from '../../render/timeline/axis';
import {
  arriveFixed,
  bezierPointAt,
  niceStep,
  roundToStep,
  sliceParamAt,
  type CurveHit,
  type CurveKeyView,
  type CurveLaneView,
  type CurveScene,
  type ValueAxis,
} from '../../render/timeline/motion/curveEditor';
import { m } from './curveEditor.messages';

// ---------------------------------------------------------------------------------------------------------------
// Snapping rules (pure)
// ---------------------------------------------------------------------------------------------------------------

/** Pointer travel before a press on a key or segment becomes a drag. */
export const DRAG_THRESHOLD_PX = 3;
/** Pointer travel that decides the Shift axis lock. */
export const AXIS_LOCK_PX = 4;
/** Smallest slice width the handles allow. */
export const MIN_SLICE_GAP = 0.01;
/** Value snap with Ctrl: about this many px between round values. */
const ROUND_VALUE_PX = 24;

export interface DragModifiers {
  readonly shift: boolean;
  readonly ctrl: boolean;
  readonly alt: boolean;
}

/** Exact beat for a pointer beat: the current grid, or the finest grid with Alt. */
export function snapTime(axis: Pick<TimeAxis, 'snap'>, beat: number, alt: boolean): Beat {
  return alt ? snapBeat(beat, FINEST_DIV) : axis.snap(beat);
}

/**
 * Value under the pointer, snapped: Alt = unrounded; Ctrl = round numbers in the display unit (about every 24 px);
 * otherwise rounded to the decimal place one pixel represents (no 135.38291 values from a mouse drag).
 */
export function snapValue(
  v: number,
  lane: { readonly axis: Pick<ValueAxis, 'perPx'>; readonly displayScale: number },
  mods: Pick<DragModifiers, 'ctrl' | 'alt'>,
): number {
  if (!Number.isFinite(v) || mods.alt) return v;
  const scale = Number.isFinite(lane.displayScale) && lane.displayScale !== 0 ? Math.abs(lane.displayScale) : 1;
  const perPxDisplay = Math.max(1e-12, lane.axis.perPx * scale);
  const stepDisplay = mods.ctrl
    ? niceStep(perPxDisplay * ROUND_VALUE_PX)
    : Math.pow(10, Math.floor(Math.log10(perPxDisplay)));
  return roundToStep(v * scale, stepDisplay) / scale;
}

/** Shift axis lock: the dominant direction once the pointer moved AXIS_LOCK_PX, else null (undecided). */
export function lockAxis(dx: number, dy: number): 'time' | 'value' | null {
  if (Math.hypot(dx, dy) < AXIS_LOCK_PX) return null;
  return Math.abs(dx) > Math.abs(dy) ? 'value' : 'time';
}

function snapUnit(u: number, mods: Pick<DragModifiers, 'ctrl' | 'alt'>): number {
  const step = mods.alt ? 0.0001 : mods.ctrl ? 0.05 : 0.01;
  return roundToStep(u, step);
}

/** New Bezier points for a handle dragged to (x progress, y normalised value); x clamped to [0, 1]. */
export function bezierWithHandle(
  p: BezierPoints,
  handle: 1 | 2,
  x: number,
  y: number,
  mods: Pick<DragModifiers, 'ctrl' | 'alt'>,
): BezierPoints {
  const sx = Math.min(1, Math.max(0, snapUnit(x, mods)));
  const sy = snapUnit(y, mods);
  return handle === 1 ? [sx, sy, p[2], p[3]] : [p[0], p[1], sx, sy];
}

/**
 * Slice after dragging one end to u, or null when Phira could not use it (f(left) = f(right): the clamped tween
 * would divide by zero) — the caller keeps the previous slice.
 */
export function sliceWithHandle(
  e: Extract<EaseValue, { kind: 'tween' }>,
  side: 'left' | 'right',
  u: number,
  mods: Pick<DragModifiers, 'ctrl' | 'alt'>,
): { readonly left: number; readonly right: number } | null {
  const v = Math.min(1, Math.max(0, snapUnit(u, mods)));
  let left = e.left;
  let right = e.right;
  if (side === 'left') left = Math.min(v, roundToStep(right - MIN_SLICE_GAP, 0.0001));
  else right = Math.max(v, roundToStep(left + MIN_SLICE_GAP, 0.0001));
  left = Math.max(0, left);
  right = Math.min(1, right);
  if (!(left < right)) return null;
  if (Ease.sliceIsDegenerate(e.id, left, right)) return null;
  return { left, right };
}

// ---------------------------------------------------------------------------------------------------------------
// Transaction bodies (pure over a TxBuilder)
// ---------------------------------------------------------------------------------------------------------------

export interface ValueEdit {
  readonly key: KeyId;
  /** 'both' on a continuous key shifts its value; on a jump key both sides shift. */
  readonly part: 'both' | 'in' | 'out';
  readonly vIn: number;
  readonly vOut: number;
}

export interface KeyDragResult {
  /** Where the grabbed key ended (after clamping between neighbours). */
  readonly beat: Beat | null;
  readonly vIn: number | null;
  readonly vOut: number | null;
}

/** True when the key follows a Stay segment: its arrive value is the stayed value and cannot be set. */
function arrivesAfterHold(tx: TxBuilder, key: KeyId): boolean {
  const prev = tx.idx.prevKey(key);
  return prev !== undefined && tx.doc.keys.get(prev)?.ease.kind === 'hold';
}

/**
 * Moves keys by dt (clamped by the core) and offsets the given values by dv. Arrive values that follow a Stay
 * segment are left alone (the core keeps them equal to the stayed value), so such a key still moves in time.
 */
export function keyDragBody(
  timeKeys: readonly KeyId[],
  dt: Beat,
  edits: readonly ValueEdit[],
  dv: number,
  grabbed: KeyId,
): (tx: TxBuilder) => KeyDragResult {
  return (tx) => {
    if (!Beats.eq(dt, Beats.ZERO) && timeKeys.length > 0) Core.keys.moveKeys(tx, { keys: timeKeys, dt });
    if (dv !== 0 && Number.isFinite(dv)) {
      for (const e of edits) {
        const inFixed = arrivesAfterHold(tx, e.key);
        if (e.part === 'in' || e.part === 'out') {
          if (e.part === 'in' && inFixed) continue;
          const base = e.part === 'in' ? e.vIn : e.vOut;
          Core.keys.setKeyValues(tx, { keys: [e.key], part: e.part, value: base + dv });
        } else if (e.vIn === e.vOut) {
          Core.keys.setKeyValues(tx, { keys: [e.key], part: 'both', value: e.vOut + dv });
        } else {
          if (!inFixed) Core.keys.setKeyValues(tx, { keys: [e.key], part: 'in', value: e.vIn + dv });
          Core.keys.setKeyValues(tx, { keys: [e.key], part: 'out', value: e.vOut + dv });
        }
      }
    }
    const k = tx.doc.keys.get(grabbed);
    return {
      beat: k?.t ?? null,
      vIn: typeof k?.vIn === 'number' ? k.vIn : null,
      vOut: typeof k?.vOut === 'number' ? k.vOut : null,
    };
  };
}

/** Applies a Bezier to one segment (the core refuses it on speed and on effect/video variables). */
export function bezierBody(segment: KeyId, p: BezierPoints): (tx: TxBuilder) => void {
  return (tx) => {
    Core.keys.setEase(tx, { segments: [segment], ease: { kind: 'bezier', p } });
  };
}

/** Changes the slice of a numbered curve on one segment. */
export function sliceBody(segment: KeyId, id: number, left: number, right: number): (tx: TxBuilder) => void {
  return (tx) => {
    Core.keys.setEase(tx, { segments: [segment], ease: { kind: 'tween', id, left, right } });
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Drag controller
// ---------------------------------------------------------------------------------------------------------------

export type StoreLike = Pick<DocStore, 'doc' | 'idx' | 'transact' | 'beginGesture'>;

/** What the controller needs from its column view. */
export interface DragEnv {
  store(): StoreLike | null;
  readonly editor: EditorStoreApi;
  readonly ui: Pick<UiService, 'setGestureHints' | 'toast'>;
  axis(): TimeAxis;
  /** Readout texts (translated). */
  readoutKey(lane: CurveLaneView, beat: Beat, vIn: number, vOut: number): string;
  readoutBezier(handle: 1 | 2, p: BezierPoints): string;
  readoutSlice(left: number, right: number): string;
  tr(ref: MessageRef): string;
  /** Redraw request after a live update or a readout change. */
  changed(): void;
}

type PressKind =
  | {
      readonly kind: 'key';
      readonly part: 'both' | 'in' | 'out';
      readonly key: CurveKeyView;
      /** Exact beat of the grabbed key when pressed. */
      readonly t: Beat;
      readonly timeKeys: readonly KeyId[];
      readonly edits: readonly ValueEdit[];
      /** The grabbed half cannot change value (arrive half after a Stay segment): time-only drag. */
      readonly valueFixed: boolean;
    }
  | { readonly kind: 'segment'; readonly start: CurveKeyView; readonly t: Beat; readonly timeKeys: readonly KeyId[] }
  | { readonly kind: 'bezier'; readonly index: number; readonly handle: 1 | 2; readonly p: BezierPoints }
  | {
      readonly kind: 'slice';
      readonly index: number;
      readonly side: 'left' | 'right';
      readonly ease: Extract<EaseValue, { kind: 'tween' }>;
    };

interface ActivePress {
  readonly p: PressKind;
  readonly lane: CurveLaneView;
  readonly scene: CurveScene;
  readonly x0: number;
  readonly y0: number;
  readonly beat0: number;
  /**
   * Selection change of a click without drag, decided on release: 'replace' reduces the selection to the item
   * (plain click on an already selected item), 'subtract' toggles it off (Ctrl+click on a selected item).
   */
  readonly onClick: ClickSelection | null;
  gesture: Gesture | null;
  lock: 'time' | 'value' | null;
  lastSlice: { readonly left: number; readonly right: number } | null;
  notes: readonly MessageRef[];
}

export interface DragReadout {
  readonly x: number;
  readonly y: number;
  readonly text: string;
}

interface ClickSelection {
  readonly mode: 'replace' | 'subtract';
  readonly kind: 'key' | 'segment';
  readonly id: KeyId;
}

function sel(ids: readonly KeyId[]): ReadonlySet<KeyId> {
  return new Set(ids);
}

/**
 * Selection on press. Plain press: an unselected item replaces the selection; a selected one keeps it (so the whole
 * selection can be dragged) and reduces it to the item on a click. Shift adds. Ctrl adds an unselected item; on a
 * selected item the toggle is decided on release (click: off; drag: Ctrl rounds the values of the drag).
 */
function pressSelection(
  editor: EditorStoreApi,
  kind: 'key' | 'segment',
  id: KeyId,
  e: Pick<TimelinePointerEvent, 'ctrl' | 'shift'>,
): ClickSelection | null {
  const state = editor.getState();
  const already = kind === 'key' ? state.selection.keys.has(id) : state.selection.segments.has(id);
  const item = kind === 'key' ? { keys: sel([id]) } : { segments: sel([id]) };
  const primary = { kind, id } as const;
  if (already) {
    state.select({ primary }, 'add');
    if (e.ctrl) return { mode: 'subtract', kind, id };
    if (e.shift) return null;
    return { mode: 'replace', kind, id };
  }
  state.select({ ...item, primary }, e.ctrl || e.shift ? 'add' : 'replace');
  return null;
}

/**
 * Turns presses on a curve-editor hit into selections and live gestures. One instance per column view; the view
 * forwards pointer events (column-local coordinates) and draws `readout()` / `warning()` on its overlay.
 */
export class CurveDragController {
  private readonly env: DragEnv;
  private press: ActivePress | null = null;
  private readoutValue: DragReadout | null = null;
  private warningValue: DragReadout | null = null;

  constructor(env: DragEnv) {
    this.env = env;
  }

  get active(): boolean {
    return this.press !== null;
  }

  get dragging(): boolean {
    return this.press?.gesture != null;
  }

  readout(): DragReadout | null {
    return this.readoutValue;
  }

  warning(): DragReadout | null {
    return this.warningValue;
  }

  /**
   * A press on an editable lane. Updates the selection (see `pressSelection`) and arms a drag. Returns false when
   * the hit is not draggable (empty space: the view starts a marquee).
   */
  begin(hit: CurveHit, lane: CurveLaneView, scene: CurveScene, e: TimelinePointerEvent): boolean {
    this.finishCleanup();
    const d = hit.detail;
    if (d.part === 'empty') return false;
    const key = lane.keys[d.index];
    if (key === undefined) return false;
    const state = this.env.editor.getState();

    if (d.part === 'key' || d.part === 'keyIn' || d.part === 'keyOut') {
      const onClick = pressSelection(this.env.editor, 'key', key.id, e);
      const now = this.env.editor.getState().selection;
      if (!now.keys.has(key.id) || !lane.editable) {
        // Nothing to drag here (read-only lane): the click decides the selection now.
        if (onClick !== null) this.applyClick(onClick);
        return true;
      }
      const part = d.part === 'keyIn' ? 'in' : d.part === 'keyOut' ? 'out' : 'both';
      // After a Stay segment the arrive half follows the stayed value: it only moves the key in time.
      const valueFixed = part === 'in' && arriveFixed(lane, d.index);
      const laneIds = new Set(lane.keys.map((k) => k.id));
      const timeKeys = [...now.keys];
      const edits: ValueEdit[] = valueFixed
        ? []
        : part === 'both'
          ? lane.keys
              .filter((k) => now.keys.has(k.id) && laneIds.has(k.id))
              .map((k) => ({ key: k.id, part: 'both' as const, vIn: k.vIn, vOut: k.vOut }))
          : [{ key: key.id, part, vIn: key.vIn, vOut: key.vOut }];
      const t = this.exactBeat(key);
      this.press = this.newPress({ kind: 'key', part, key, t, timeKeys, edits, valueFixed }, lane, scene, e, onClick);
      return true;
    }

    // Segments and their handles.
    let onClick: ClickSelection | null = null;
    if (d.part === 'segment') {
      onClick = pressSelection(this.env.editor, 'segment', key.id, e);
    } else if (!state.selection.segments.has(key.id)) {
      state.select({ segments: sel([key.id]), primary: { kind: 'segment', id: key.id } }, 'replace');
    }
    const now = this.env.editor.getState().selection;
    if (!lane.editable) {
      if (onClick !== null) this.applyClick(onClick);
      return true;
    }
    if (d.part === 'segment') {
      if (!now.segments.has(key.id)) return true;
      const ids = new Set<KeyId>();
      const store = this.env.store();
      for (const s of now.segments) {
        ids.add(s);
        const next = store?.idx.nextKey(s);
        if (next !== undefined) ids.add(next);
      }
      const t = this.exactBeat(key);
      this.press = this.newPress({ kind: 'segment', start: key, t, timeKeys: [...ids] }, lane, scene, e, onClick);
      return true;
    }
    if (d.part === 'bezier') {
      if (key.ease.kind !== 'bezier' || !lane.bezierHandles) return true;
      this.press = this.newPress(
        { kind: 'bezier', index: d.index, handle: d.handle, p: key.ease.p },
        lane,
        scene,
        e,
        null,
      );
      return true;
    }
    if (key.ease.kind !== 'tween' || !('side' in d)) return true;
    this.press = this.newPress({ kind: 'slice', index: d.index, side: d.side, ease: key.ease }, lane, scene, e, null);
    return true;
  }

  /** The key's exact beat in the document (its f64 drawing beat only when the key vanished meanwhile). */
  private exactBeat(k: CurveKeyView): Beat {
    return this.env.store()?.doc.keys.get(k.id)?.t ?? Beats.fromF64(k.beat, FINEST_DIV);
  }

  private newPress(
    p: PressKind,
    lane: CurveLaneView,
    scene: CurveScene,
    e: TimelinePointerEvent,
    onClick: ClickSelection | null,
  ): ActivePress {
    return {
      p,
      lane,
      scene,
      x0: e.x,
      y0: e.y,
      beat0: e.beat,
      onClick,
      gesture: null,
      lock: null,
      lastSlice: null,
      notes: [],
    };
  }

  private meta(p: PressKind): TxnMeta {
    switch (p.kind) {
      case 'key':
        return { label: { key: m.txMoveKeys, params: { count: Math.max(p.timeKeys.length, p.edits.length) } } };
      case 'segment':
        return { label: { key: m.txMoveSegment } };
      case 'bezier':
        return { label: { key: m.txBezier } };
      case 'slice':
        return { label: { key: m.txSlice } };
    }
  }

  /** Pointer moved with the button held. */
  move(e: TimelinePointerEvent): void {
    const pr = this.press;
    if (pr === null) return;
    const dx = e.x - pr.x0;
    const dy = e.y - pr.y0;
    if (pr.gesture === null) {
      const handle = pr.p.kind === 'bezier' || pr.p.kind === 'slice';
      if (!handle && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      if (handle && dx === 0 && dy === 0) return;
      const store = this.env.store();
      if (store === null) return;
      pr.gesture = store.beginGesture(this.meta(pr.p));
      this.env.ui.setGestureHints([
        { chord: 'Shift', label: { key: m.hintAxisLock } },
        { chord: 'Ctrl', label: { key: m.hintRound } },
        { chord: 'Alt', label: { key: m.hintNoSnap } },
        { chord: 'Escape', label: { key: m.hintCancel } },
      ]);
    }
    if (e.shift) pr.lock ??= lockAxis(dx, dy);
    else pr.lock = null;
    const lockTime = e.shift && pr.lock === 'value';
    const lockValue = e.shift && pr.lock === 'time';
    const mods: DragModifiers = { shift: e.shift, ctrl: e.ctrl, alt: e.alt };
    switch (pr.p.kind) {
      case 'key':
        this.moveKey(pr, pr.p, e, lockTime, lockValue, mods);
        break;
      case 'segment':
        this.moveSegment(pr, pr.p, e, lockTime, mods);
        break;
      case 'bezier':
        this.moveBezier(pr, pr.p, e, lockTime, lockValue, mods);
        break;
      case 'slice':
        this.moveSlice(pr, pr.p, e, mods);
        break;
    }
    this.env.changed();
  }

  private apply<R>(pr: ActivePress, build: (tx: TxBuilder) => R, at: TimelinePointerEvent): R | null {
    const g = pr.gesture;
    if (g === null) return null;
    const out = g.update(build);
    if (!out.ok) {
      this.warningValue = { x: at.x, y: at.y, text: this.env.tr(out.reason) };
      return null;
    }
    pr.notes = out.notes;
    this.warningValue = null;
    return out.value;
  }

  private moveKey(
    pr: ActivePress,
    p: Extract<PressKind, { kind: 'key' }>,
    e: TimelinePointerEvent,
    lockTime: boolean,
    lockValue: boolean,
    mods: DragModifiers,
  ): void {
    const axis = this.env.axis();
    const k = p.key;
    let dt = Beats.ZERO;
    if (!lockTime) {
      const target = snapTime(axis, k.beat + (e.beat - pr.beat0), mods.alt);
      dt = Beats.sub(target, p.t);
    }
    let dv = 0;
    if (!lockValue && !p.valueFixed) {
      const base = p.part === 'in' ? k.vIn : k.vOut;
      const va = pr.lane.axis;
      const raw = base + (va.xToValue(e.x) - va.xToValue(pr.x0));
      dv = snapValue(raw, pr.lane, mods) - base;
    }
    const res = this.apply(pr, keyDragBody(p.timeKeys, dt, p.edits, dv, k.id), e);
    if (res?.beat != null) {
      const vIn = res.vIn ?? k.vIn;
      const vOut = res.vOut ?? k.vOut;
      this.readoutValue = { x: e.x, y: e.y, text: this.env.readoutKey(pr.lane, res.beat, vIn, vOut) };
    }
    // Sideways on a fixed arrive half: explain why the value does not follow.
    if (p.valueFixed && !lockValue && Math.abs(e.x - pr.x0) >= AXIS_LOCK_PX * 2) {
      this.warningValue = { x: e.x, y: e.y, text: this.env.tr({ key: m.arriveFixed }) };
    }
  }

  private moveSegment(
    pr: ActivePress,
    p: Extract<PressKind, { kind: 'segment' }>,
    e: TimelinePointerEvent,
    lockTime: boolean,
    mods: DragModifiers,
  ): void {
    if (lockTime) return;
    const axis = this.env.axis();
    const k = p.start;
    const target = snapTime(axis, k.beat + (e.beat - pr.beat0), mods.alt);
    const dt = Beats.sub(target, p.t);
    const res = this.apply(pr, keyDragBody(p.timeKeys, dt, [], 0, k.id), e);
    if (res?.beat != null) {
      this.readoutValue = { x: e.x, y: e.y, text: this.env.readoutKey(pr.lane, res.beat, k.vOut, k.vOut) };
    }
  }

  private moveBezier(
    pr: ActivePress,
    p: Extract<PressKind, { kind: 'bezier' }>,
    e: TimelinePointerEvent,
    lockTime: boolean,
    lockValue: boolean,
    mods: DragModifiers,
  ): void {
    const axis = this.env.axis();
    const pt = bezierPointAt(pr.lane, p.index, e.x, e.y, axis, pr.scene.time);
    const start = pr.lane.keys[p.index];
    if (pt === null || start === undefined) {
      this.warningValue = { x: e.x, y: e.y, text: this.env.tr({ key: m.flatSegment }) };
      return;
    }
    const cur = p.handle === 1 ? [p.p[0], p.p[1]] : [p.p[2], p.p[3]];
    const x = lockTime ? (cur[0] as number) : pt[0];
    const y = lockValue ? (cur[1] as number) : pt[1];
    const next = bezierWithHandle(p.p, p.handle, x, y, mods);
    if (this.apply(pr, bezierBody(start.id, next), e) !== null) {
      this.readoutValue = { x: e.x, y: e.y, text: this.env.readoutBezier(p.handle, next) };
    }
  }

  private moveSlice(
    pr: ActivePress,
    p: Extract<PressKind, { kind: 'slice' }>,
    e: TimelinePointerEvent,
    mods: DragModifiers,
  ): void {
    const axis = this.env.axis();
    const start = pr.lane.keys[p.index];
    if (start === undefined) return;
    const u = sliceParamAt(pr.lane, p.index, e.y, axis, pr.scene.time);
    const next = sliceWithHandle(p.ease, p.side, u, mods);
    if (next === null) {
      this.warningValue = { x: e.x, y: e.y, text: this.env.tr({ key: m.sliceRefused }) };
      return;
    }
    if (pr.lastSlice !== null && pr.lastSlice.left === next.left && pr.lastSlice.right === next.right) return;
    if (this.apply(pr, sliceBody(start.id, p.ease.id, next.left, next.right), e) !== null) {
      pr.lastSlice = next;
      this.readoutValue = { x: e.x, y: e.y, text: this.env.readoutSlice(next.left, next.right) };
    }
  }

  /** Button released: commit the drag (one undo step), or finish a click. */
  end(): void {
    const pr = this.press;
    if (pr === null) return;
    if (pr.gesture !== null) {
      pr.gesture.commit();
      const note = pr.notes[0];
      if (note !== undefined) this.env.ui.toast(note, { kind: 'info' });
    } else if (pr.onClick !== null) {
      this.applyClick(pr.onClick);
    }
    this.finishCleanup();
    this.env.changed();
  }

  /** The selection change of a click without drag (see `ClickSelection`). */
  private applyClick(c: ClickSelection): void {
    const state = this.env.editor.getState();
    if (c.mode === 'subtract') {
      state.select(c.kind === 'key' ? { keys: sel([c.id]) } : { segments: sel([c.id]) }, 'subtract');
      return;
    }
    state.select(
      c.kind === 'key'
        ? { keys: sel([c.id]), segments: new Set(), primary: { kind: 'key', id: c.id } }
        : { keys: new Set(), segments: sel([c.id]), primary: { kind: 'segment', id: c.id } },
      'replace',
    );
  }

  /** Esc, lost capture, column removed: revert the live gesture. */
  cancel(): void {
    const pr = this.press;
    if (pr === null) return;
    pr.gesture?.cancel();
    this.finishCleanup();
    this.env.changed();
  }

  private finishCleanup(): void {
    const had = this.press?.gesture != null;
    this.press = null;
    this.readoutValue = null;
    this.warningValue = null;
    if (had) this.env.ui.setGestureHints(null);
  }
}
